import type { SitePageLink } from '@hunk/shared/api/pages';
import { formatMoney } from '@hunk/shared/common';
import { LogIn, LogOut, Menu, Package, Search, Settings, ShoppingBag, UserRound, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router';

import { AccessibilityWidget } from '@/components/AccessibilityWidget';
import { Logo } from '@/components/Logo';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useCart } from '@/hooks/useCart';
import { useSession } from '@/hooks/useSession';
import { useShop } from '@/hooks/useShop';
import { cn } from '@/lib/utils';

/**
 * Sticky header, two rows:
 *   red top line (like bb365) - start (right): the content pages as white pills (phones: a ☰ menu with them);
 *                               end (left): admin, account / login, cart
 *   black bar (like hunk.co.il) - logo + search; the categories under it
 * Which pages are in the top line is set per page in the admin (עמודי תוכן).
 */
export function SiteHeader() {
  const { status, session, isMiniApp, logout } = useSession();
  const { count, itemsTotal } = useCart();
  const { categories, pages } = useShop();
  const topLine = pages.filter((p) => p.inTopLine);
  const [params] = useSearchParams();
  const navigate = useAppNavigate();
  const { pathname } = useLocation();
  const inAdmin = pathname === '/admin' || pathname.startsWith('/admin/');
  const [q, setQ] = useState(params.get('q') ?? '');
  /** the last term this field put in the URL - so the URL catching up never overwrites what is being typed */
  const pushed = useRef<string | null>(null);
  useEffect(() => {
    const p = params.get('q') ?? '';
    if (p !== pushed.current) setQ(p); // changed from outside (back button, a category link)
  }, [params]);

  /** the results for `term` on the home page (replace = no history entry per keystroke) */
  const goSearch = useCallback(
    (term: string, replace: boolean) => {
      pushed.current = term;
      navigate(term ? `/?q=${encodeURIComponent(term)}` : '/', { replace });
    },
    [navigate]
  );
  // search while typing: 300 ms after the last key. From another page the first jump to the results is a normal
  // navigation (back returns there); while already on the results each keystroke just replaces the URL.
  const urlQ = (params.get('q') ?? '').trim();
  useEffect(() => {
    const term = q.trim();
    if (term === urlQ) return;
    if (!term && pathname !== '/') return; // emptied the field on another page: stay there
    const t = setTimeout(() => goSearch(term, pathname === '/'), 300);
    return () => clearTimeout(t);
  }, [q, urlQ, pathname, goSearch]);
  // Enter / the button: at once
  const search = (e: FormEvent) => {
    e.preventDefault();
    goSearch(q.trim(), pathname === '/');
  };

  const user = session?.user;
  const name = user?.tgUsername ? `@${user.tgUsername}` : (user?.fullName ?? user?.email ?? '');
  const isAdmin = status === 'authenticated' && session?.role === 'admin';
  const showPages = !inAdmin && topLine.length > 0;

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-black/95 pt-[var(--tg-top)] backdrop-blur">
      <div className="brand-grad relative border-b border-black/30">
        <div className="mx-auto flex h-11 max-w-[1280px] items-center gap-2 px-2 md:px-4">
          {showPages && (
            <>
              <PagesMenu pages={topLine} />
              <nav aria-label="מידע על החנות" className="scrollbar-none hidden min-w-0 items-center gap-2 overflow-x-auto py-1 md:flex">
                {topLine.map((p) => (
                  <NavLink
                    key={p.key}
                    to={p.path}
                    className={({ isActive }) =>
                      cn(
                        'shrink-0 rounded-full px-3 py-1 text-sm font-extrabold whitespace-nowrap shadow-sm transition-transform hover:scale-105',
                        isActive ? 'bg-black text-white ring-2 ring-white' : 'bg-white text-black'
                      )
                    }
                  >
                    {p.title}
                  </NavLink>
                ))}
              </nav>
            </>
          )}

          {/* end of the line (left in RTL): admin, account, cart */}
          <div className="ms-auto flex shrink-0 items-center gap-1 text-white">
            {isAdmin && (
              <Link to="/admin" title="ניהול" className="flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-bold hover:bg-black/20">
                <Settings className="size-4" />
                <span className="hidden sm:inline">ניהול</span>
              </Link>
            )}
            <AccountMenu status={status} name={name} isMiniApp={isMiniApp} isAdmin={isAdmin} onLogout={logout} />
            <Link to="/cart" className="relative flex items-center gap-2 rounded-full p-1.5 hover:bg-black/20" aria-label={`סל קניות, ${count} פריטים`}>
              <ShoppingBag className="size-6" />
              {count > 0 && (
                <span className="text-primary absolute -top-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full bg-white text-[11px] font-extrabold shadow">
                  {count}
                </span>
              )}
              {count > 0 && <span className="hidden text-sm font-bold lg:inline">{formatMoney(itemsTotal)}</span>}
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-3 px-3 md:h-20 md:gap-6 md:px-4">
        <Link to="/" aria-label="HUNK - דף הבית" className="shrink-0">
          <Logo />
        </Link>

        {/** biome-ignore lint/a11y/useSemanticElements: search form */}
        <form onSubmit={search} className="mx-auto flex h-10 w-full max-w-xl min-w-0" role="search">
          <div className="relative min-w-0 flex-1">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="חיפוש מוצרים"
              className="h-full w-full min-w-0 rounded-s-full border border-e-0 border-white/40 bg-black px-4 text-sm outline-none placeholder:text-neutral-400 focus:border-white"
            />
            {q && (
              <button type="button" onClick={() => setQ('')} className="absolute inset-y-0 end-2 flex items-center text-neutral-400 hover:text-white" aria-label="ניקוי">
                <X className="size-4" />
              </button>
            )}
          </div>
          <button type="submit" className="brand-grad brand-grad-hover flex w-11 shrink-0 items-center justify-center rounded-e-full text-white" aria-label="חיפוש">
            <Search className="size-4" />
          </button>
        </form>

        {/* left of the search (RTL end): accessibility menu + WhatsApp */}
        <div className="flex shrink-0 items-center gap-1.5 md:gap-2">
          <AccessibilityWidget />
          <WhatsAppButton />
        </div>
      </div>

      {!inAdmin && categories.length > 0 && (
        <nav className="scrollbar-none mx-auto flex max-w-[1280px] overflow-x-auto px-1 md:px-2">
          <CatLink to="/" label="כל המוצרים" end />
          {categories.map((c) => (
            <CatLink key={c.id} to={`/category/${encodeURIComponent(c.slug)}`} label={c.name} />
          ))}
        </nav>
      )}
    </header>
  );
}

/** phones: the content pages behind a ☰ button at the start of the red line (closes on a link, outside click, Esc) */
function PagesMenu({ pages }: { pages: SitePageLink[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={ref} className="md:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="pages-menu"
        aria-label={open ? 'סגירת התפריט' : 'תפריט'}
        className="flex items-center gap-1.5 rounded-full px-2 py-1 font-bold text-white hover:bg-black/20"
      >
        {open ? <X className="size-6" /> : <Menu className="size-6" />}
        <span className="text-sm">תפריט</span>
      </button>
      {open && (
        <nav id="pages-menu" aria-label="מידע על החנות" className="absolute inset-x-0 top-full z-50 border-b border-white/10 bg-neutral-950 shadow-2xl">
          {pages.map((p) => (
            <NavLink
              key={p.key}
              to={p.path}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn('flex items-center border-b border-white/5 px-4 py-3.5 text-base font-semibold', isActive ? 'text-primary' : 'text-white hover:bg-white/5')
              }
            >
              {p.title}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

function CatLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'shrink-0 border-b-2 px-3 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors md:px-4',
          isActive ? 'border-primary text-white' : 'border-transparent text-neutral-400 hover:text-white'
        )
      }
    >
      {label}
    </NavLink>
  );
}

function AccountMenu({ status, name, isMiniApp, isAdmin, onLogout }: { status: string; name: string; isMiniApp: boolean; isAdmin: boolean; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (status !== 'authenticated') {
    return isMiniApp ? null : (
      <Link to="/login" className="flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 text-sm font-bold hover:bg-black/20" title="כניסה">
        <LogIn className="size-5" />
        <span className="hidden md:inline">כניסה</span>
      </Link>
    );
  }
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full p-1.5 hover:bg-black/20"
        aria-label="החשבון שלי"
        aria-expanded={open}
      >
        <UserRound className="size-6" />
        <span className="hidden max-w-32 truncate text-sm lg:inline" dir="ltr">
          {name}
        </span>
      </button>
      {open && (
        <div className="bg-card absolute end-0 top-full z-50 mt-1 w-48 overflow-hidden rounded-lg border text-white shadow-xl">
          <MenuLink to="/orders" icon={<Package className="size-4" />} label="ההזמנות שלי" onClick={() => setOpen(false)} />
          {isAdmin && <MenuLink to="/admin" icon={<Settings className="size-4" />} label="ניהול החנות" onClick={() => setOpen(false)} />}
          {!isMiniApp && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-sm hover:bg-white/5"
            >
              <LogOut className="size-4" />
              יציאה
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, icon, label, onClick }: { to: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-white/5">
      {icon}
      {label}
    </Link>
  );
}

export function SiteFooter() {
  const { categories, pages } = useShop();
  return (
    <footer className="mt-12 border-t border-white/10 bg-black">
      <div className="bg-primary py-3 text-center text-lg font-extrabold text-white md:text-2xl">HUNK בבעלות הבוס הגדול</div>
      <div className="mx-auto grid max-w-[1280px] gap-6 px-4 py-8 text-sm text-neutral-400 md:grid-cols-3">
        <div className="flex flex-col items-center gap-2 md:items-start">
          <Logo />
          <p className="max-w-xs text-center md:text-start">יבוא אישי של אביזרים לפיסט וחומרי סיכה במחירים הכי זולים בארץ. משלוח דיסקרטי עד הבית או איסוף עצמי.</p>
        </div>
        <nav aria-label="קטגוריות" className="flex flex-wrap justify-center gap-x-4 gap-y-1 md:justify-start">
          {categories.map((c) => (
            <Link key={c.id} to={`/category/${encodeURIComponent(c.slug)}`} className="hover:text-white">
              {c.name}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col items-center gap-1 md:items-end">
          {pages.map((p) => (
            <Link key={p.key} to={p.path} className="hover:text-white">
              {p.title}
            </Link>
          ))}
          <Link to="/orders" className="hover:text-white">
            ההזמנות שלי
          </Link>
          <Link to="/cart" className="hover:text-white">
            סל הקניות
          </Link>
        </div>
      </div>
      <p className="border-t border-white/10 py-4 text-center text-xs text-neutral-500">האתר מיועד לבני 18 ומעלה בלבד · © {new Date().getFullYear()} HUNK</p>
    </footer>
  );
}
